import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Clock, Search as SearchIcon, Activity, ChevronRight } from 'lucide-react';
import { getClinics } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { humanizeInterval, relativeDays } from '../utils/format';
import StatTile from '../components/ui/StatTile';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

const SORTS = [
  { key: 'events', label: 'Most events' },
  { key: 'locations', label: 'Most locations' },
  { key: 'recent', label: 'Most recent' },
];

function ClinicsList() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('events');
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Clinics | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getClinics({ sort, search: search || undefined })
      .then((res) => { if (active) { setClinics(res.data.data.clinics); setError(null); } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load clinics')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [sort, search]);

  const activeNow = clinics.filter((c) => c.isActiveNow).length;
  const totalLocations = new Set();
  clinics.forEach((c) => { if (c.locationCount) totalLocations.add(c.clinicName); });

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h1 className="page-title mb-1">Competitor Clinics</h1>
        <p className="text-sm text-ink-500">
          Every advertiser we've seen — click one to see where they go, how often, and what's likely next.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatTile icon={Building2} label="Clinics tracked" value={clinics.length} tone="dental" />
        <StatTile icon={Activity} label="Active right now" value={activeNow} tone="green" />
        <StatTile icon={MapPin} label="Sorted by" value={SORTS.find((s) => s.key === sort)?.label} tone="accent" />
      </div>

      {/* Controls */}
      <div className="card flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 text-ink-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="input pl-9"
            placeholder="Search clinics…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                sort === s.key
                  ? 'bg-dental-600 text-white'
                  : 'bg-white ring-1 ring-ink-200 text-ink-600 hover:bg-ink-100'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="card bg-red-50 ring-red-200">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : clinics.length === 0 ? (
        <EmptyState icon={Building2} title="No clinics found" message="Try a different search, or fetch new events first." />
      ) : (
        <div className="card p-0 overflow-hidden divide-y divide-ink-100">
          {clinics.map((c) => (
            <Link
              key={c.clinicName}
              to={`/clinics/${encodeURIComponent(c.clinicName)}`}
              className="flex items-center gap-4 p-4 hover:bg-ink-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-xl bg-dental-50 text-dental-600 ring-1 ring-dental-100 flex items-center justify-center shrink-0 font-bold">
                {c.clinicName?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-ink-900 truncate">{c.clinicName}</p>
                  {c.isActiveNow && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> live
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-500 mt-0.5">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.locationCount} location{c.locationCount === 1 ? '' : 's'}</span>
                  {c.avgIntervalDays != null && (
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{humanizeInterval(c.avgIntervalDays)}</span>
                  )}
                  {c.topTreatment && <span className="chip py-0.5">{c.topTreatment}</span>}
                </div>
              </div>
              <div className="text-right shrink-0 hidden sm:block">
                <p className="text-lg font-bold text-ink-900">{c.eventCount}</p>
                <p className="text-[11px] text-ink-400">events · {relativeDays(c.daysSinceLast)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-ink-300 group-hover:text-dental-500 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClinicsList;
